import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BrandLogo from "./BrandLogo";
import { useLang } from "../data/i18n";

const NAV_LINKS = [
    { key: "accueil", hash: "#home", id: "accueil" },
    { key: "experiences", hash: "#experiences", id: "experiences" },
    { key: "about", hash: "#about", id: "about" },
    { key: "offres", hash: "#offres", id: "offres" },
    { key: "addons", hash: "#addons", id: "addons" },
    { key: "contact", hash: "#contact", id: "contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t, prefix, isEn, pathname } = useLang();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Same page, other language ("/" ↔ "/en", "/slug" ↔ "/en/slug")
    const altPath = isEn
        ? pathname.replace(/^\/en/, "") || "/"
        : pathname === "/"
          ? "/en"
          : `/en${pathname}`;
    const frHref = isEn ? altPath : pathname;
    const enHref = isEn ? pathname : altPath;

    return (
        <>
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-cream/92 backdrop-blur-xl border-b border-ocean/10 shadow-sm"
                        : "bg-transparent"
                }`}
                data-testid="site-header"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 md:py-4 flex items-center justify-between">
                    <a
                        href={`${prefix}/#home`}
                        className="flex items-center gap-3"
                        data-testid="nav-logo"
                    >
                        <BrandLogo variant="primary" height={54} />
                    </a>

                    <nav className="hidden lg:flex items-center gap-8">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.hash}
                                href={`${prefix}/${l.hash}`}
                                className="text-[11px] uppercase tracking-[0.24em] font-semibold text-ink/80 hover:text-coral underline-grow transition-colors"
                                data-testid={`nav-${l.id}`}
                            >
                                {t.nav[l.key]}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        {/* Prominent quote CTA — straight to the contact form */}
                        <a
                            href={`${prefix}/#contact`}
                            className="hidden lg:inline-flex items-center gap-2 bg-coral text-cream px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold rounded-full hover:bg-coral-600 hover:scale-[1.03] transition-all duration-300 shadow-[0_8px_24px_rgba(235,103,65,0.4)]"
                            data-testid="header-cta-quote"
                        >
                            {t.nav.quote}
                            <span aria-hidden="true">→</span>
                        </a>
                        {/* Language switcher */}
                        <div
                            className="hidden lg:flex items-center border-2 border-ocean/20 rounded-full overflow-hidden text-[10px] uppercase tracking-[0.18em] font-bold"
                            data-testid="lang-switch"
                        >
                            <a
                                href={frHref}
                                aria-current={!isEn ? "true" : undefined}
                                className={`px-3 py-1.5 transition-colors ${
                                    !isEn
                                        ? "bg-ocean text-cream"
                                        : "text-ocean/70 hover:text-coral"
                                }`}
                                data-testid="lang-switch-fr"
                            >
                                FR
                            </a>
                            <a
                                href={enHref}
                                aria-current={isEn ? "true" : undefined}
                                className={`px-3 py-1.5 transition-colors ${
                                    isEn
                                        ? "bg-ocean text-cream"
                                        : "text-ocean/70 hover:text-coral"
                                }`}
                                data-testid="lang-switch-en"
                            >
                                EN
                            </a>
                        </div>
                        <button
                            onClick={() => setOpen(true)}
                            className="lg:hidden text-ink p-2"
                            aria-label="Open menu"
                            data-testid="mobile-menu-toggle"
                        >
                            <Menu size={26} />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-cream"
                        data-testid="mobile-menu"
                    >
                        <div className="flex justify-between items-center px-5 py-4 border-b border-ocean/10">
                            <BrandLogo variant="primary" height={48} />
                            <button
                                onClick={() => setOpen(false)}
                                className="text-ink p-2"
                                data-testid="mobile-menu-close"
                                aria-label="Close menu"
                            >
                                <X size={26} />
                            </button>
                        </div>
                        <nav className="flex flex-col p-8 gap-6">
                            {NAV_LINKS.map((l, i) => (
                                <motion.a
                                    key={l.hash}
                                    href={`${prefix}/${l.hash}`}
                                    onClick={() => setOpen(false)}
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.05 + i * 0.05 }}
                                    className="font-serif text-3xl text-ocean hover:text-coral"
                                >
                                    {t.nav[l.key]}
                                </motion.a>
                            ))}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="flex items-center gap-3 pt-4 mt-4 border-t border-ocean/10"
                                data-testid="lang-switch-mobile"
                            >
                                <a
                                    href={frHref}
                                    onClick={() => setOpen(false)}
                                    className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold border-2 rounded-full transition-colors ${
                                        !isEn
                                            ? "bg-ocean text-cream border-ocean"
                                            : "text-ocean/70 border-ocean/25 hover:text-coral"
                                    }`}
                                    data-testid="lang-switch-fr-mobile"
                                >
                                    Français
                                </a>
                                <a
                                    href={enHref}
                                    onClick={() => setOpen(false)}
                                    className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold border-2 rounded-full transition-colors ${
                                        isEn
                                            ? "bg-ocean text-cream border-ocean"
                                            : "text-ocean/70 border-ocean/25 hover:text-coral"
                                    }`}
                                    data-testid="lang-switch-en-mobile"
                                >
                                    English
                                </a>
                            </motion.div>
                            <motion.a
                                href={`${prefix}/#contact`}
                                onClick={() => setOpen(false)}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="mt-4 inline-flex justify-center items-center gap-2 bg-coral text-cream px-6 py-4 text-xs uppercase tracking-[0.2em] font-bold rounded-full shadow-[0_8px_24px_rgba(235,103,65,0.4)]"
                                data-testid="header-cta-quote-mobile"
                            >
                                {t.nav.quote}
                                <span aria-hidden="true">→</span>
                            </motion.a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
